'use client';

import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';

interface ComplianceSection {
  id: string;
  icon: string;
  title: string;
  content: string;
  details?: string[];
}

const PIPAComplianceContent = () => {
  const complianceSections: ComplianceSection[] = [
    {
      id: 'what_is_pipa',
      icon: 'DocumentTextIcon',
      title: 'What is PIPA?',
      content: 'The Personal Information Protection Act (PIPA) is Bermuda\'s comprehensive data protection law, designed to safeguard your personal information. At Legacy, we embrace PIPA\'s principles because protecting your most precious memories and messages is at the heart of everything we do.',
      details: [
        'PIPA ensures your personal data is collected, used, and stored responsibly',
        'It gives you control over who sees your information',
        'It requires organizations to be transparent about data practices',
        'It mandates strong security measures to protect your privacy'
      ]
    },
    {
      id: 'your_control',
      icon: 'LockClosedIcon',
      title: 'You Control Your Data',
      content: 'Your legacy is yours alone. We believe in complete transparency and control. Only the information you choose to share is shared—and only with the people you designate as messengers or memorial recipients.',
      details: [
        'You decide what personal information to include in your profile',
        'You choose which messengers receive your messages',
        'You control who can view your memorial pages',
        'You can update or delete your information at any time',
        'No data is ever sold or shared with third parties for marketing'
      ]
    },
    {
      id: 'data_security',
      icon: 'ShieldCheckIcon',
      title: '256-Bit Encryption & Security',
      content: 'Your messages, photos, videos, and personal information are protected with industry-leading 256-bit encryption—the same standard used by banks and government institutions. This means your data is secured both in transit and at rest.',
      details: [
        'All data transmissions use 256-bit SSL/TLS encryption',
        'Your messages are stored in encrypted format on secure servers',
        'Access controls ensure only authorized individuals can view your content',
        'Regular security audits and updates protect against emerging threats',
        'Multi-factor authentication options for enhanced account security'
      ]
    },
    {
      id: 'data_collection',
      icon: 'ClipboardDocumentCheckIcon',
      title: 'What We Collect & Why',
      content: 'We only collect information necessary to provide our service—helping you create and deliver meaningful messages to your loved ones. We treat your data with the care and respect it deserves.',
      details: [
        'Account information: Name, email, and password to create your secure account',
        'Message content: The messages, photos, videos, and audio you choose to preserve',
        'Messenger details: Contact information for people you designate to receive messages',
        'Delivery preferences: When and how you want messages delivered',
        'Usage data: Technical information to improve service reliability and security'
      ]
    },
    {
      id: 'data_sharing',
      icon: 'UserGroupIcon',
      title: 'Limited, Purposeful Sharing',
      content: 'Your trust is sacred to us. We never sell your data. Information is only shared in specific, limited circumstances—and always with your explicit consent or legal requirement.',
      details: [
        'With your messengers: Only when you designate them to receive specific messages',
        'With service providers: Trusted partners who help us deliver emails or store data securely (all bound by strict confidentiality agreements)',
        'For legal compliance: Only when required by Bermuda law or valid legal process',
        'With your consent: If you explicitly authorize sharing for a specific purpose'
      ]
    },
    {
      id: 'your_rights',
      icon: 'HandRaisedIcon',
      title: 'Your Rights Under PIPA',
      content: 'PIPA grants you important rights over your personal information, and we\'re committed to honoring them fully. Your data belongs to you, and we\'re here to help you exercise your rights.',
      details: [
        'Right to Access: Request a copy of all personal data we hold about you',
        'Right to Correction: Update or correct any inaccurate information',
        'Right to Deletion: Request deletion of your data (subject to legal retention requirements)',
        'Right to Portability: Receive your data in a structured, commonly used format',
        'Right to Object: Object to certain types of data processing',
        'Right to Withdraw Consent: Change your mind about data sharing at any time'
      ]
    },
    {
      id: 'bermuda_compliance',
      icon: 'BuildingLibraryIcon',
      title: 'Bermuda Law Compliance',
      content: 'As a Bermuda-based service, we operate under the jurisdiction of Bermuda\'s robust data protection framework. We maintain full compliance with PIPA and work closely with the Office of the Privacy Commissioner for Bermuda.',
      details: [
        'Registered with the Bermuda Privacy Commissioner',
        'Regular compliance audits and assessments',
        'Data protection officer available for privacy concerns',
        'Transparent privacy policies updated to reflect legal requirements',
        'Incident response procedures aligned with PIPA notification requirements'
      ]
    },
    {
      id: 'data_retention',
      icon: 'ArchiveBoxIcon',
      title: 'Data Retention & Legacy',
      content: 'Your messages are designed to last—but you remain in control. We retain your data for as long as your account is active and as needed to fulfill your delivery instructions. You can modify or delete content at any time before delivery.',
      details: [
        'Active accounts: Data retained indefinitely to preserve your legacy',
        'Delivered messages: Stored according to your delivery preferences',
        'Inactive accounts: We\'ll contact you before taking any action',
        'Account deletion: You can request full deletion at any time',
        'Legal holds: Some data may be retained if required by law'
      ]
    }
  ];

  const quickLinks = [
    { label: 'Privacy Policy', href: '/', icon: 'DocumentTextIcon' },
    { label: 'Terms of Service', href: '/', icon: 'ClipboardDocumentListIcon' },
    { label: 'Security Practices', href: '/', icon: 'ShieldCheckIcon' },
    { label: 'Contact Privacy Team', href: '/homepage#contact', icon: 'EnvelopeIcon' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Icon name="ShieldCheckIcon" size={20} />
            <span>PIPA Compliant</span>
          </div>
          <h1 className="font-jakarta font-bold text-4xl md:text-5xl lg:text-6xl text-foreground mb-6 leading-tight">
            Your Privacy, Your Legacy,
            <span className="text-primary"> Your Control</span>
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">
            At Legacy, we honor your trust by protecting your most precious memories with industry-leading security and full compliance with Bermuda's Personal Information Protection Act (PIPA).
          </p>
        </div>
      </section>

      {/* Key Promise Section */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 border border-border">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name="HeartIcon" size={24} className="text-primary" />
              </div>
              <div>
                <h2 className="font-jakarta font-bold text-2xl text-foreground mb-4">
                  Our Promise to You
                </h2>
                <p className="text-muted-foreground leading-relaxed text-lg">
                  Only the data you share is shared—and only with those you decide. Your messages, photos, and memories are protected with 256-bit encryption and stored securely. We never sell your data. We never share it without your permission. Your legacy is yours alone, and we're honored to help you preserve it.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance Sections */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto space-y-8">
          {complianceSections.map((section) => (
            <div
              key={section.id}
              className="bg-white rounded-2xl shadow-md p-8 md:p-10 border border-border hover:shadow-lg transition-shadow"
            >
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon name={section.icon as any} size={24} className="text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-jakarta font-bold text-2xl text-foreground mb-3">
                    {section.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    {section.content}
                  </p>
                  {section.details && (
                    <ul className="space-y-3">
                      {section.details.map((detail, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <Icon name="CheckCircleIcon" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                          <span className="text-muted-foreground">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Quick Links Section */}
      <section className="py-16 px-6 bg-muted/30">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-jakarta font-bold text-3xl text-foreground text-center mb-12">
            Related Resources
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {quickLinks.map((link) => (
              <Link
                key={link.label}
                href={link.href}
                className="bg-white rounded-xl p-6 border border-border hover:border-primary hover:shadow-lg transition-all group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <Icon name={link.icon as any} size={24} className="text-primary" />
                  </div>
                  <span className="font-semibold text-foreground group-hover:text-primary transition-colors">
                    {link.label}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl p-12 border border-primary/20">
            <Icon name="ChatBubbleLeftRightIcon" size={48} className="text-primary mx-auto mb-6" />
            <h2 className="font-jakarta font-bold text-3xl text-foreground mb-4">
              Questions About Your Privacy?
            </h2>
            <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
              We're here to help. If you have any questions about how we protect your data, your rights under PIPA, or our privacy practices, please don't hesitate to reach out.
            </p>
            <Link
              href="/homepage#contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-full font-semibold hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl"
            >
              <Icon name="EnvelopeIcon" size={20} />
              <span>Contact Our Privacy Team</span>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PIPAComplianceContent;